import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { EnvironmentSystemBase } from '../EnvironmentSystemBase';
import { BirdConfig } from '../types';

type BirdEntity = {
    group: THREE.Group;
    leftWing?: THREE.Group;
    rightWing?: THREE.Group;
    position: THREE.Vector3;
    velocity: THREE.Vector3;
    wingPhase: number;
    wingSpeed: number;
    flockRadius: number;
    detailed: boolean;
};

const DEFAULTS: BirdConfig = {
    count: 200,
    speed: 0.06,
    height: 17,
    detailedThreshold: 100,
    bounds: { xz: 40, yMin: 12, yMax: 32 }
};

export class BirdFlockSystem extends EnvironmentSystemBase {
    public config: BirdConfig;
    private birds: BirdEntity[] = [];
    private readonly bodyMaterial = new THREE.MeshLambertMaterial({
        color: 0x252b31,
        flatShading: true,
    });
    private readonly wingMaterial = new THREE.MeshLambertMaterial({
        color: 0x343d46,
        side: THREE.DoubleSide,
        flatShading: true,
    });
    private readonly bodyGeometry = this.createBodyGeometry();
    private readonly leftWingGeometry = this.createWingGeometry(-1);
    private readonly rightWingGeometry = this.createWingGeometry(1);

    constructor(name: string, cfg?: Partial<BirdConfig>) {
        super(name);
        this.config = { ...DEFAULTS, ...(cfg || {}) };
    }

    setConfig(cfg: Partial<BirdConfig>): void {
        const oldCount = this.config.count;
        this.config = { ...this.config, ...cfg };
        if (this.config.count !== oldCount) this.ensurePopulation();
    }

    protected build(): void {
        this.group.clear();
        this.birds = [];
        this.ensurePopulation();
    }

    private ensurePopulation(): void {
        // Remove all
        this.group.clear();
        this.birds = [];
        const useDetailed = this.config.count <= this.config.detailedThreshold;
        for (let i = 0; i < this.config.count; i++) {
            const bird = this.createBird(useDetailed);
            this.birds.push(bird);
            this.group.add(bird.group);
        }
    }

    protected onUpdate(dt: number): void {
        if (!this.enabled) return;
        if (this.birds.length === 0) return;
        const speed = 0.5 + this.config.speed * 0.5;
        const bounds = this.config.bounds;
        const time = performance.now() * 0.001;
        const simplified = this.config.count > 500;

        this.birds.forEach((bird, index) => {
            if (!simplified) {
                let separation = new THREE.Vector3();
                let alignment = new THREE.Vector3();
                let cohesion = new THREE.Vector3();
                let neighbors = 0;
                const step = Math.max(1, Math.floor(this.birds.length / Math.min(this.birds.length, this.config.count > 100 ? 5 : 10)));
                for (let i = 0; i < this.birds.length; i += step) {
                    if (i === index) continue;
                    const other = this.birds[i];
                    const dist = bird.position.distanceTo(other.position);
                    if (dist < bird.flockRadius) {
                        neighbors++;
                        if (dist < 3) {
                            const diff = new THREE.Vector3().subVectors(bird.position, other.position);
                            diff.normalize();
                            diff.divideScalar(dist);
                            separation.add(diff);
                        }
                        alignment.add(other.velocity);
                        cohesion.add(other.position);
                    }
                }
                if (neighbors > 0) {
                    separation.normalize().multiplyScalar(0.5);
                    alignment.divideScalar(neighbors).normalize().multiplyScalar(0.3);
                    cohesion.divideScalar(neighbors).sub(bird.position).normalize().multiplyScalar(0.2);
                    bird.velocity.add(separation).add(alignment).add(cohesion);
                }
            } else {
                bird.velocity.add(new THREE.Vector3((Math.random()-0.5)*0.2, (Math.random()-0.5)*0.1, (Math.random()-0.5)*0.2));
            }

            bird.velocity.add(new THREE.Vector3((Math.random()-0.5)*0.1, (Math.random()-0.5)*0.05, (Math.random()-0.5)*0.1));
            if (bird.position.x > bounds.xz) bird.velocity.x -= 0.1;
            if (bird.position.x < -bounds.xz) bird.velocity.x += 0.1;
            if (bird.position.z > bounds.xz) bird.velocity.z -= 0.1;
            if (bird.position.z < -bounds.xz) bird.velocity.z += 0.1;
            if (bird.position.y > this.config.height + (bounds.yMax - this.config.height)) bird.velocity.y -= 0.1;
            if (bird.position.y < this.config.height - (this.config.height - bounds.yMin)) bird.velocity.y += 0.1;

            bird.velocity.clampLength(speed * 0.5, speed * 2);
            bird.position.add(bird.velocity.clone().multiplyScalar(this.config.speed));
            bird.group.position.copy(bird.position);
            const dir = bird.velocity.clone().normalize();
            bird.group.lookAt(bird.position.clone().add(dir));

            if (bird.detailed && bird.leftWing && bird.rightWing) {
                // Time based rather than frame based: the flock now flaps at the same
                // cadence on 60 Hz desktops and 120 Hz phones.
                bird.wingPhase += bird.wingSpeed * Math.min(dt, 0.05);
                const flap = Math.sin(bird.wingPhase);
                const intensity = 0.52;
                // Mirrored rotations around proper shoulder pivots keep both wing tips
                // moving together. The previous PI offset flipped the right wing over.
                bird.leftWing.rotation.z = flap * intensity;
                bird.rightWing.rotation.z = -flap * intensity;
                const sweep = 0.04 + Math.abs(flap) * 0.08;
                bird.leftWing.rotation.y = sweep;
                bird.rightWing.rotation.y = -sweep;
            }
        });
    }

    private createBird(detailed: boolean): BirdEntity {
        const group = new THREE.Group();
        const s = this.config.sizeScale ?? 1;
        group.name = 'FlockingBird';

        // The body gives the flock depth and a stable front/back reading. Low polygon
        // counts deliberately preserve the light, graphic style of the map.
        const body = new THREE.Mesh(this.bodyGeometry, this.bodyMaterial);
        group.add(body);

        const leftWing = new THREE.Group();
        leftWing.position.set(-0.1, 0.015, 0.04);
        leftWing.add(new THREE.Mesh(this.leftWingGeometry, this.wingMaterial));
        const rightWing = new THREE.Group();
        rightWing.position.set(0.1, 0.015, 0.04);
        rightWing.add(new THREE.Mesh(this.rightWingGeometry, this.wingMaterial));
        group.add(leftWing, rightWing);

        group.scale.setScalar(s);
        return {
            group,
            leftWing,
            rightWing,
            position: new THREE.Vector3((Math.random()-0.5)*60, this.config.height + Math.random()*6, (Math.random()-0.5)*60),
            velocity: new THREE.Vector3((Math.random()-0.5)*2, (Math.random()-0.5)*0.5, (Math.random()-0.5)*2),
            wingPhase: Math.random() * Math.PI * 2,
            wingSpeed: 3.6 + Math.random() * 1.4,
            flockRadius: 8 + Math.random() * 4,
            // Preserve the previous effective behaviour: both historical geometry
            // branches animated their wings, including the nominally simplified one.
            detailed: true
        };
    }

    private createBodyGeometry(): THREE.BufferGeometry {
        const body = new THREE.SphereGeometry(0.18, 8, 5);
        body.scale(0.72, 0.62, 1.65);
        body.deleteAttribute('uv');

        const head = new THREE.SphereGeometry(0.105, 7, 4);
        head.translate(0, 0.012, 0.285);
        head.deleteAttribute('uv');

        const beak = new THREE.ConeGeometry(0.05, 0.16, 5);
        beak.rotateX(Math.PI / 2);
        beak.translate(0, 0.005, 0.405);
        beak.deleteAttribute('uv');

        const tail = this.createTailGeometry();
        tail.translate(0, -0.005, -0.24);

        // One shared static geometry keeps each detailed bird at three draw calls:
        // body silhouette + two independently animated wings.
        const merged = mergeGeometries([body, head, beak, tail], false);
        body.dispose();
        head.dispose();
        beak.dispose();
        tail.dispose();
        if (!merged) throw new Error('Could not create flocking bird body geometry');
        return merged;
    }

    private createWingGeometry(side: -1 | 1): THREE.BufferGeometry {
        const positions = new Float32Array([
            0, 0, 0.14,
            side * 0.72, 0, 0.02,
            side * 0.54, 0, -0.2,
            side * 0.12, 0, -0.1,
        ]);
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        geometry.setIndex(side < 0 ? [0, 1, 2, 0, 2, 3] : [0, 2, 1, 0, 3, 2]);
        geometry.computeVertexNormals();
        return geometry;
    }

    private createTailGeometry(): THREE.BufferGeometry {
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute([
            -0.11, 0, 0,
            0.11, 0, 0,
            -0.22, 0, -0.38,
            0, 0, -0.28,
            0.22, 0, -0.38,
        ], 3));
        geometry.setIndex([0, 2, 3, 0, 3, 1, 1, 3, 4]);
        geometry.computeVertexNormals();
        return geometry;
    }

    dispose(): void {
        this.bodyGeometry.dispose();
        this.leftWingGeometry.dispose();
        this.rightWingGeometry.dispose();
        this.bodyMaterial.dispose();
        this.wingMaterial.dispose();
        this.group.clear();
        this.birds = [];
    }
}
